/** The app frame from the Figma: grey header and sidebar, white panel with the rounded top-left corner. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { navigationOf, type EntrySummary } from '@hotpot/core';
import { useT } from '../i18n.js';
import { Link, useRouter } from '../router.js';
import { useBackend, useMaybeSite, useSession } from '../state.js';
import { navItem, paths, RepoImage, Status } from './ui.js';
import { LangSwitch, SiteFooter } from './Footer.js';
import { DraftsBar } from './Drafts.js';

export function Layout({ children, sidebar = true, narrow }: { children: ReactNode; sidebar?: boolean; narrow?: boolean }) {
  const site = useMaybeSite();
  const [menuOpen, setMenuOpen] = useState(false);
  const { loc } = useRouter();
  useEffect(() => setMenuOpen(false), [loc.path]);
  const withSidebar = sidebar && !!site;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('hp-sidebar') === 'collapsed';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem('hp-sidebar', c ? 'open' : 'collapsed');
      } catch {
        /* storage blocked: the choice lasts for this page only */
      }
      return !c;
    });
  return (
    <div className={`app${withSidebar ? '' : ' no-sidebar'}${withSidebar && collapsed ? ' sidebar-collapsed' : ''}`}>
      <Header onMenu={withSidebar ? () => setMenuOpen((o) => !o) : undefined} menuOpen={menuOpen} />
      {withSidebar && <Sidebar open={menuOpen} collapsed={collapsed} onToggle={toggle} />}
      <main className={`panel${narrow ? ' narrow' : ''}`} id="main">
        {site && <DraftsBar />}
        {children}
      </main>
      {!withSidebar && <SiteFooter />}
    </div>
  );
}

function Header({ onMenu, menuOpen }: { onMenu?: () => void; menuOpen: boolean }) {
  const { t } = useT();
  const { me, setMe } = useSession();
  const backend = useBackend();
  const site = useMaybeSite();
  const { navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <header className="header">
      <Link className="logo" to={site ? paths.site(site.handle.site.id) : '/'} aria-label="Hotpot CMS">
        <img src={`${import.meta.env.BASE_URL}logo.svg`} alt="HOTPOT CMS" width={304} height={68} />
      </Link>
      {site ? <Search /> : <span />}
      {onMenu && (
        <button className="menu-btn" onClick={onMenu} aria-expanded={menuOpen} aria-label={t('collections')}>
          {menuOpen ? '✕' : '☰'}
        </button>
      )}
      <div ref={ref} className="header-right">
        <LangSwitch />
        <button className="hat" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={t('account')}>
          <img src={`${import.meta.env.BASE_URL}chef-hat.webp`} alt="" />
        </button>
        {open && me && (
          <div className="menu" role="menu">
            <div className="who">
              {me.user.name && <div style={{ color: 'var(--ink)' }}>{me.user.name}</div>}
              {me.user.email}
              {me.user.studio && <div>Hotpot Studio</div>}
            </div>
            {(me.sites.length > 1 || me.user.studio) && (
              <Link className="link-btn ink" to="/" onClick={() => setOpen(false)}>
                {t('allSites')}
              </Link>
            )}
            <Link className="link-btn ink" to="/account" onClick={() => setOpen(false)}>
              {t('account')}
            </Link>
            <Link className="link-btn ink" to="/privacy" onClick={() => setOpen(false)}>
              {t('privacy')}
            </Link>
            <button
              className="link-btn ink"
              onClick={async () => {
                await backend.logout();
                setMe(null);
                navigate('/login?out=1', { force: true });
              }}
            >
              {t('logout')}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function Sidebar({ open, collapsed, onToggle }: { open: boolean; collapsed: boolean; onToggle: () => void }) {
  const { t } = useT();
  const site = useMaybeSite()!;
  const { me } = useSession();
  const { loc } = useRouter();
  const id = site.handle.site.id;
  const groups = navigationOf(site.config);
  const isActive = (to: string) => loc.path === to || loc.path.startsWith(to + '/');
  const role = site.handle.site.role;
  const multi = (me?.sites.length ?? 0) > 1 || me?.user.studio;
  return (
    <aside className={`sidebar${open ? ' open' : ''}`} aria-label={t('collections')}>
      <button className="sidebar-toggle" onClick={onToggle} aria-expanded={!collapsed} title={collapsed ? t('showPanel') : t('hidePanel')} aria-label={collapsed ? t('showPanel') : t('hidePanel')}>
        {collapsed ? '»' : '«'}
      </button>
      <div className="site-switch">
        {multi && (
          <Link className="back" to="/">
            ← {t('allSites')}
          </Link>
        )}
        <p className="site-name">
          <Link to={paths.site(id)} className={`navlink${loc.path === paths.site(id) ? ' active' : ''}`}>
            {site.handle.site.name}
          </Link>
        </p>
      </div>
      <nav>
        {groups.map((g, i) => (
          <div key={i}>
            <h2>{g.label || t('collections')}</h2>
            <ul>
              {g.items.map((item) => {
                const n = navItem(site.config, id, item);
                if (!n) return null;
                return (
                  <li key={item}>
                    <Link to={n.to} className={`navlink${isActive(n.to) ? ' active' : ''}`} aria-current={isActive(n.to) ? 'page' : undefined}>
                      {n.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="bottom">
        {site.publish && (
          <div style={{ marginBottom: 10 }}>
            <Status status={site.publish.status} small url={site.handle.site.url || undefined} />
          </div>
        )}
        <Link to={paths.media(id)} className={`navlink${isActive(paths.media(id)) ? ' active' : ''}`}>
          {t('media')}
        </Link>
        <Link to={paths.history(id)} className={`navlink${isActive(paths.history(id)) ? ' active' : ''}`}>
          {t('history')}
        </Link>
        {(role === 'owner' || role === 'studio') && (
          <Link to={paths.people(id)} className={`navlink${isActive(paths.people(id)) ? ' active' : ''}`}>
            {t('users')}
          </Link>
        )}
        {(site.config.settings?.length ?? 0) > 0 && (
          <Link to={`/s/${id}/settings`} className={`navlink${isActive(`/s/${id}/settings`) || site.config.settings!.some((s) => { const n = navItem(site.config, id, s); return n && isActive(n.to); }) ? ' active' : ''}`}>
            {t('globalSettings')}
          </Link>
        )}
      </div>
    </aside>
  );
}

interface Hit {
  to: string;
  title: string;
  kind: string;
  thumb?: string;
}

function Search() {
  const { t } = useT();
  const site = useMaybeSite()!;
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  const [all, setAll] = useState<{ collection: string; label: string; entries: EntrySummary[] }[]>([]);
  const [sel, setSel] = useState(0);
  const id = site.handle.site.id;

  useEffect(() => {
    if (!focus) return;
    let alive = true;
    const folders = site.config.collections.filter((c) => c.type === 'folder' && !c.folder.includes('{{locale}}'));
    Promise.all(folders.map(async (c) => ({ collection: c.name, label: c.label_singular ?? c.label, entries: await site.list(c.name).catch(() => []) }))).then((r) => alive && setAll(r));
    return () => {
      alive = false;
    };
  }, [focus, site]);

  const hits = useMemo<Hit[]>(() => {
    const s = norm(q.trim());
    if (!s) return [];
    const out: Hit[] = [];
    for (const c of site.config.collections) {
      if (c.type === 'files') {
        for (const f of c.files) if (norm(f.label).includes(s)) out.push({ to: paths.file(id, c.name, f.name), title: f.label, kind: c.label });
      } else if (norm(c.label).includes(s)) out.push({ to: paths.list(id, c.name), title: c.label, kind: '' });
    }
    for (const group of all) {
      for (const e of group.entries) {
        if (norm(`${e.title} ${e.subtitle}`).includes(s)) out.push({ to: paths.entry(id, group.collection, e.slug), title: e.title, kind: group.label, thumb: e.thumbnail });
      }
    }
    return out.slice(0, 30);
  }, [q, all, site.config, id]);

  const go = (h: Hit) => {
    setQ('');
    setFocus(false);
    (document.activeElement as HTMLElement)?.blur();
    navigate(h.to);
  };

  return (
    <div className={`search${focus ? ' open' : ''}`}>
      <input
        className="input"
        type="search"
        placeholder={t('search')}
        aria-label={t('search')}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setSel(0);
        }}
        onFocus={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setSel((s) => Math.min(s + 1, hits.length - 1));
          else if (e.key === 'ArrowUp') setSel((s) => Math.max(s - 1, 0));
          else if (e.key === 'Enter' && hits[sel]) go(hits[sel]!);
          else if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
        }}
      />
      {focus && q.trim() && (
        <div className="search-results" role="listbox">
          {hits.length === 0 && <p className="muted small" style={{ margin: '6px 18px' }}>{t('searchNoResults')}</p>}
          {hits.map((h, i) => (
            <a
              key={h.to}
              href="#"
              role="option"
              aria-selected={i === sel}
              className={i === sel ? 'sel' : ''}
              onMouseDown={(e) => {
                e.preventDefault();
                go(h);
              }}
            >
              {h.thumb ? <RepoImage path={h.thumb} /> : <span className="ph" />}
              <span>{h.title}</span>
              <span className="k">{h.kind}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function norm(s: string): string {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
